/**
 * The Admin screens' data layer.
 *
 * Everything here goes through `@/api/client` (one fetch wrapper, cookies
 * always attached) and the app's single `QueryClient`. There is no second
 * transport and no second cache.
 *
 * ## I14 — keys are write-only
 *
 * This module is where that invariant is either kept or lost, so it is stated
 * as code rather than as a convention:
 *
 * - Nothing here ever *reads* a key. `GET /api/admin/llm-config` and
 *   `GET /api/admin/service-keys` answer with `has_key` and `key_source` and
 *   no credential material, and the response types
 *   ({@link LlmConfig}, {@link ServiceKey}) have no field one could be put in.
 *   The query cache therefore cannot hold a key.
 * - A key only ever travels *outward*, as the `api_key` property of a request
 *   body built by {@link llmConfigBody} or {@link serviceKeyBody}. Those two
 *   functions are pure and exported precisely so a test can assert the
 *   property is **absent** when the operator left the field blank — the
 *   difference between "leave the stored key alone" and "wipe it" is one
 *   missing key in a JSON object, and it is not a difference to leave
 *   implicit.
 * - The one plaintext credential the product ever returns is a newly minted
 *   API key. {@link useCreateApiKey} does not write it into the query cache,
 *   and its caller drops it from component state the moment the copy-once
 *   dialog closes.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'
import type {
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyCreated,
  FallbackEntry,
  LlmConfig,
  LlmConfigTestResult,
  LlmConfigUpdateRequest,
  LlmRole,
  ReasoningMode,
  ServiceKey,
  ServiceKeyName,
  ServiceKeyUpdateRequest,
  User,
  UserCreateRequest,
  UserPatchRequest,
} from './types'

// ---------------------------------------------------------------------------
// Role vocabulary
// ---------------------------------------------------------------------------

/**
 * The roles a stage actually calls, in the order the cards render.
 *
 * Mirrors `server.app.llm.router.LAUNCH_ROLES` plus `OPTIONAL_ROLES`, and
 * migration `0001`'s seed. `vlm_ocr` joined it when `services/sign_vision`
 * began calling it (`docs/DEVICE_MARKS.md` §5) — it is configurable but not
 * required, and the readiness panel reports it as optional rather than missing.
 */
export const CONFIGURABLE_LLM_ROLES: readonly LlmRole[] = [
  'reasoner',
  'judge',
  'embedder',
  'vlm_ocr',
]

/** Configurable, but the platform drafts every response without it. */
export const OPTIONAL_LLM_ROLES: readonly LlmRole[] = ['vlm_ocr']

/**
 * Called by nothing yet, and deliberately not seeded.
 *
 * `PUT /api/admin/llm-config/{role}` answers 400, so the screen shows it as a
 * row without controls: configuring a role no stage calls would put a
 * credential in the database with nothing to use it.
 */
export const DEFERRED_LLM_ROLES: readonly LlmRole[] = ['classifier']

export interface LlmRoleCopy {
  title: string
  description: string
}

/** What each role is for, so a card is legible without opening the plan. */
export const LLM_ROLE_COPY: Record<LlmRole, LlmRoleCopy> = {
  reasoner: {
    title: 'Analysis and drafting',
    description:
      'Ground classification, comparison limbs and response drafting. Needs strong JSON and reasoning.',
  },
  judge: {
    title: 'Evaluation',
    description:
      'Scores drafts in the gold-set harness. Never the model that wrote the draft, and never mixed across an A/B.',
  },
  embedder: {
    title: 'Embeddings',
    description:
      'Corpus and law chunk vectors. Changing this model means re-embedding the corpus, not mixing.',
  },
  classifier: {
    title: 'Classification and enrichment',
    description: 'Doc type, boilerplate and relevance passes. No stage calls it yet.',
  },
  vlm_ocr: {
    title: 'Document OCR (vision)',
    description:
      'Reads a mark image and proposes what it shows, so an attorney has something to check rather than a blank. A proposal only: the description is stamped as a machine reading and a draft may not argue from it until a person accepts it.',
  },
}

/** Providers with a registered adapter, offered as suggestions — not a whitelist. */
export const KNOWN_PROVIDERS: readonly string[] = ['fireworks', 'anthropic', 'echo']

/** The service credentials the card lists. See {@link SERVICE_KEY_NAMES}. */
export const SERVICE_KEY_LABELS: Record<ServiceKeyName, string> = {
  smtp: 'SMTP password',
  firecrawl: 'Firecrawl API key',
}

/**
 * What each credential is spent on, and the variable it falls back to when
 * cleared. Shown beside the field so an operator hunting one does not have
 * to read the code to learn which feature stops when it goes.
 */
export const SERVICE_KEY_HINTS: Record<ServiceKeyName, string> = {
  smtp: 'Sign-in codes are emailed with this password. Clear it to fall back to TMVERSE_SMTP_PASSWORD.',
  firecrawl:
    'The Research card on a case reads web pages through Firecrawl with this key. Clear it to fall back to TMVERSE_FIRECRAWL_API_KEY.',
}

// ---------------------------------------------------------------------------
// The write-only key edit
// ---------------------------------------------------------------------------

/**
 * What the operator did with a password field.
 *
 * Three outcomes, and the whole point of naming them is that the middle one is
 * not the absence of the others: leaving a field blank means *keep the stored
 * key*, and clearing it is a separate, deliberate act with its own button.
 */
export type KeyEdit =
  | { kind: 'keep' }
  | { kind: 'set'; value: string }
  | { kind: 'clear' }

export const KEEP_KEY: KeyEdit = { kind: 'keep' }
export const CLEAR_KEY: KeyEdit = { kind: 'clear' }

/**
 * Read a password input.
 *
 * Blank — including whitespace someone pasted by accident — is `keep`. There
 * is no input string that means "clear": that is {@link CLEAR_KEY}, reached
 * from a button that says so.
 */
export function keyEditFromInput(input: string): KeyEdit {
  const value = input.trim()
  return value ? { kind: 'set', value } : KEEP_KEY
}

/** Attach `api_key` to a body — or leave it off entirely, which is the point. */
function withKey<T extends object>(body: T, edit: KeyEdit): T & { api_key?: string } {
  switch (edit.kind) {
    case 'keep':
      // Absent, not null and not empty: the server's `_apply_key` reads a
      // missing field as "leave the stored value alone".
      return body
    case 'clear':
      return { ...body, api_key: '' }
    case 'set':
      // Trimmed here as well as in `keyEditFromInput`: a key pasted with a
      // trailing newline authenticates against nothing, and the failure looks
      // like a bad credential rather than a bad paste.
      return { ...body, api_key: edit.value.trim() }
  }
}

/** The editable half of an `llm_config` row — everything except the credential. */
export interface LlmConfigDraft {
  provider: string
  model_id: string
  max_tokens: number
  timeout_s: number
  reasoning: ReasoningMode
  fallback_order: FallbackEntry[]
  enabled: boolean
}

/**
 * Build the `PUT /api/admin/llm-config/{role}` body.
 *
 * A full upsert, so every non-key field is always sent. `api_key` appears only
 * for `set` and `clear` (I14).
 */
export function llmConfigBody(draft: LlmConfigDraft, edit: KeyEdit): LlmConfigUpdateRequest {
  return withKey(
    {
      provider: draft.provider.trim(),
      model_id: draft.model_id.trim(),
      max_tokens: draft.max_tokens,
      timeout_s: draft.timeout_s,
      reasoning: draft.reasoning,
      fallback_order: draft.fallback_order
        .filter((entry) => entry.provider.trim() && entry.model_id.trim())
        .map((entry) => ({
          provider: entry.provider.trim(),
          model_id: entry.model_id.trim(),
          base_url: entry.base_url,
        })),
      enabled: draft.enabled,
    },
    edit,
  )
}

/**
 * Build the `PUT /api/admin/service-keys/{name}` body.
 *
 * `{}` for `keep` — a service key row has nothing else to write, so keeping the
 * credential means sending an empty object rather than nothing at all.
 */
export function serviceKeyBody(edit: KeyEdit): Partial<ServiceKeyUpdateRequest> {
  return withKey({}, edit)
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const adminKeys = {
  llmConfig: ['admin', 'llm-config'] as const,
  serviceKeys: ['admin', 'service-keys'] as const,
  users: ['admin', 'users'] as const,
  apiKeys: ['admin', 'api-keys'] as const,
}

// ---------------------------------------------------------------------------
// Models & keys
// ---------------------------------------------------------------------------

export function useLlmConfigs(): UseQueryResult<LlmConfig[]> {
  return useQuery<LlmConfig[]>({
    queryKey: adminKeys.llmConfig,
    queryFn: () => api.get<LlmConfig[]>('/api/admin/llm-config'),
  })
}

export function useSaveLlmConfig() {
  const queryClient = useQueryClient()
  return useMutation<LlmConfig, unknown, { role: LlmRole; body: LlmConfigUpdateRequest }>({
    mutationFn: ({ role, body }) =>
      api.put<LlmConfig>(`/api/admin/llm-config/${encodeURIComponent(role)}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.llmConfig })
    },
  })
}

/**
 * The Test button.
 *
 * A real round-trip through the adapter at temperature 0 against a two-property
 * JSON schema — it proves the credential decrypts and the provider answers,
 * which no amount of "saved" can. The endpoint never raises: a failure comes
 * back as `{ok: false, error}` already scrubbed of key material server-side.
 */
export function useTestLlmConfig() {
  return useMutation<LlmConfigTestResult, unknown, LlmRole>({
    mutationFn: (role) =>
      api.post<LlmConfigTestResult>(`/api/admin/llm-config/${encodeURIComponent(role)}/test`),
  })
}

export function useServiceKeys(): UseQueryResult<ServiceKey[]> {
  return useQuery<ServiceKey[]>({
    queryKey: adminKeys.serviceKeys,
    queryFn: () => api.get<ServiceKey[]>('/api/admin/service-keys'),
  })
}

export function useSaveServiceKey() {
  const queryClient = useQueryClient()
  return useMutation<
    ServiceKey,
    unknown,
    { name: ServiceKeyName; body: Partial<ServiceKeyUpdateRequest> }
  >({
    mutationFn: ({ name, body }) =>
      api.put<ServiceKey>(`/api/admin/service-keys/${encodeURIComponent(name)}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.serviceKeys })
    },
  })
}

// ---------------------------------------------------------------------------
// Users & access
// ---------------------------------------------------------------------------

export function useUsers(): UseQueryResult<User[]> {
  return useQuery<User[]>({
    queryKey: adminKeys.users,
    queryFn: () => api.get<User[]>('/api/admin/users'),
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation<User, unknown, UserCreateRequest>({
    mutationFn: (body) => api.post<User>('/api/admin/users', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users })
    },
  })
}

export function usePatchUser() {
  const queryClient = useQueryClient()
  return useMutation<User, unknown, { id: string; body: UserPatchRequest }>({
    mutationFn: ({ id, body }) => api.patch<User>(`/api/admin/users/${encodeURIComponent(id)}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.users })
    },
  })
}

export function useApiKeys(): UseQueryResult<ApiKey[]> {
  return useQuery<ApiKey[]>({
    queryKey: adminKeys.apiKeys,
    queryFn: () => api.get<ApiKey[]>('/api/admin/api-keys'),
  })
}

/**
 * Mint a key.
 *
 * The response is the only one in the product carrying a plaintext credential.
 * It is *not* seeded into the cache here — `invalidateQueries` re-reads the
 * list from the server, which answers with the prefix and nothing else.
 */
export function useCreateApiKey() {
  const queryClient = useQueryClient()
  return useMutation<ApiKeyCreated, unknown, ApiKeyCreateRequest>({
    mutationFn: (body) => api.post<ApiKeyCreated>('/api/admin/api-keys', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apiKeys })
    },
  })
}

/** Revoke — the server deactivates rather than deletes, so events still resolve (I12). */
export function useRevokeApiKey() {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: (id) => api.delete<void>(`/api/admin/api-keys/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.apiKeys })
    },
  })
}

// ---------------------------------------------------------------------------
// Formatting shared by the admin tables
// ---------------------------------------------------------------------------

/** A timestamp as a local date and time, or an em dash for "never". */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return '—'
  return when.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
